import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ChevronDown, ExternalLink } from "lucide-react";
import { Button } from "../ui/button";
import { LOGO_URL } from "../../lib/branches";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../ui/dropdown-menu";

const mainLinks = [
  { to: "/", label: "Home" },
  { to: "/sermons", label: "Sermons" },
  { to: "/events", label: "Events" },
  { to: "/prayers", label: "Prayer" },
  { to: "/youth", label: "Youth" },
  { to: "/bible-school", label: "Bible School" },
  { to: "/locations", label: "Locations" },
];

const involvedLinks = [
  { to: "/membership", label: "Membership" },
  { to: "/join-team", label: "Join a Team" },
  { to: "/homecell", label: "Join a Homecell" },
  { to: "/counselling", label: "Counselling" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-bone/80 border-b border-black/5" data-testid="site-navbar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="nav-logo">
          <div className="w-12 h-12 bg-white border border-border overflow-hidden flex items-center justify-center">
            <img src={LOGO_URL} alt="ETI logo" className="w-full h-full object-contain" />
          </div>
          <div className="hidden sm:block leading-tight">
            <p className="font-heading font-bold text-ink text-sm">El-Shaddai Tabernacle</p>
            <p className="text-xs text-muted_ink">International · Occupy and Build</p>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {mainLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-olive" : "text-ink/70 hover:text-olive"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-ink/70 hover:text-olive outline-none" data-testid="nav-get-involved">
              Get Involved <ChevronDown size={14} />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {involvedLinks.map((l) => (
                <DropdownMenuItem key={l.to} asChild>
                  <Link to={l.to} data-testid={`nav-dropdown-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>{l.label}</Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <a
            href="https://shop.eti.co.za"
            target="_blank"
            rel="noreferrer"
            data-testid="nav-store-link"
            className="text-sm font-medium text-ink/70 hover:text-olive flex items-center gap-1"
          >
            Store <ExternalLink size={12} />
          </a>
        </nav>

        <div className="flex items-center gap-2 shrink-0">
          <Button
            onClick={() => nav("/giving")}
            data-testid="nav-give-button"
            className="hidden sm:inline-flex bg-terracotta hover:bg-terracotta-hover text-white rounded-none px-6 h-10 font-semibold"
          >
            Give
          </Button>
          <button
            className="xl:hidden p-2 text-ink"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-black/5 bg-bone max-h-[85vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-6 py-4 flex flex-col gap-2">
            {mainLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `py-2 text-base font-medium ${isActive ? "text-olive" : "text-ink/80"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <div className="mt-4 pt-3 border-t border-border">
              <p className="overline text-muted_ink mb-2">Get Involved</p>
              {involvedLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block py-2 text-sm ${isActive ? "text-olive font-semibold" : "text-ink/70"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
            <a
              href="https://shop.eti.co.za"
              target="_blank"
              rel="noreferrer"
              className="mt-3 pt-3 border-t border-border py-2 text-base font-medium text-ink/80 flex items-center gap-2"
            >
              Store <ExternalLink size={14} />
            </a>
            <Button
              onClick={() => { setOpen(false); nav("/giving"); }}
              className="bg-terracotta hover:bg-terracotta-hover text-white rounded-none mt-3"
            >
              Give
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
